import { z } from 'zod';

/**
 * Central registry for the settings page's feature flags and visual tweaks.
 *
 * `kind` decides where a value is persisted: `flag` entries are backend-persisted
 * (global, survives restarts — see packages/backend/src/routes/feature-flags.ts),
 * `tweak` entries live in the browser's localStorage only (see
 * packages/frontend/src/lib/tweak-store.ts). `type` decides which control the
 * settings UI renders and is independent of `kind` — a flag and a tweak can both be
 * `enum`, for instance.
 *
 * Language split: keys, enum option *values*, and everything persisted to the DB
 * or localStorage are English (or untranslatable domain terms like `palier` and
 * `noyau`). Everything the UI *renders* — `label`, `description`,
 * and each option's `label` — is French.
 *
 * `description` is written for the end user (one or two sentences, always
 * visible under the label). The former developer prose lives in a code comment
 * above each def.
 */

/**
 * The screen group a def belongs to on the settings page. Declared in display
 * order by {@link SETTING_SECTIONS}.
 */
export type SettingSection = 'entreprise' | 'matrice' | 'setup' | 'systeme';

/** A settings-page section: its title and the "where it shows" line under it. */
export type SettingSectionMeta = { id: SettingSection; title: string; where: string };

/** Sections in display order. The three first make the "Affichage" tab, `systeme` the "Génération & système" one. */
export const SETTING_SECTIONS: readonly SettingSectionMeta[] = [
  {
    id: 'entreprise',
    title: 'Toutes les pages entreprise',
    where: 'Page entreprise, assistant de création, matrice et export.',
  },
  {
    id: 'matrice',
    title: 'Matrice',
    where: "Grille de la page matrice. L'export reprend la couleur des en-têtes.",
  },
  {
    id: 'setup',
    title: 'Setup de singularité',
    where: 'Onglet « Paramètres entreprise » de la page entreprise, tableau des 6 étapes.',
  },
  {
    id: 'systeme',
    title: 'Génération & système',
    where:
      'Le rôle simulé ne change que ce navigateur ; les modèles et le serveur de connexion s’appliquent à tous les utilisateurs.',
  },
];

type BaseDef = {
  /** Unique across the registry, kebab-case. Also the DB primary key for flags. */
  key: string;
  label: string;
  /** User-facing text, one or two French sentences, always visible under the label. */
  description: string;
  /** Which settings-page section hosts the entry. */
  section: SettingSection;
  kind: 'flag' | 'tweak';
};

/**
 * One selectable value of an `enum` def. `value` is the persisted identifier —
 * it lands in the DB or localStorage, keys lookup records in consuming
 * components, and is passed verbatim to external tools (e.g. `claude --model`),
 * so it stays English. `label` is the French text rendered in the `<select>`.
 *
 * A `{ value, label }` pair rather than a parallel `Record<value, label>`
 * because the record can drift — a label for a value that no longer exists,
 * or a value with no label. A pair cannot.
 */
export type EnumOption = { value: string; label: string };

export type BooleanDef = BaseDef & { type: 'boolean'; default: boolean };
export type NumberDef = BaseDef & {
  type: 'number';
  default: number;
  min?: number;
  max?: number;
  step?: number;
};
export type StringDef = BaseDef & { type: 'string'; default: string };
export type EnumDef = BaseDef & {
  type: 'enum';
  default: string;
  options: readonly EnumOption[];
};

/** A single registry entry. Rendered by the settings UI from `DEFS`. */
export type Def = BooleanDef | NumberDef | StringDef | EnumDef;

/** A parsed flag/tweak value. */
export type FlagValue = boolean | number | string;

/**
 * The source of truth. Add an entry and it shows up in the settings UI, in the
 * `section` it declares. Array order is the display order within a section, and
 * sections follow the order of {@link SETTING_SECTIONS}.
 */
export const DEFS = [
  // Developer documentation (the former `description`, kept verbatim):
  // Aperçu depuis la page Réglages uniquement — remplace tous les usages d'accent génériques
  // bg-bleu-nuit/text-bleu-nuit/etc. dans les pages liées à une entreprise par la couleur du
  // noyau sélectionné, via une surcharge de propriété personnalisée CSS sur ce sous-arbre.
  // N'est PAS branché sur la valeur réelle et persistée company.noyau — c'est un toggle
  // visuel d'aperçu, pas la vraie fonctionnalité de thématisation par entreprise (qui
  // demanderait sa propre passe). N'affecte jamais NoyauPicker ni aucun autre affichage
  // d'une valeur de noyau réelle. Le thème d’en-tête « Dégradé noyau » lit le noyau réel,
  // pas cet aperçu.
  {
    key: 'matrix-noyaux-accent',
    label: "Couleur d'accent",
    description:
      "Remplace le bleu nuit des titres, onglets, boutons et liens par la couleur d'un noyau, pour voir l'app aux couleurs d'une entreprise. Simple aperçu : la couleur enregistrée de l'entreprise ne change pas.",
    section: 'entreprise',
    kind: 'tweak',
    type: 'enum',
    default: 'bleu-nuit',
    options: [
      { value: 'bleu-nuit', label: 'Bleu nuit' },
      { value: 'bleu-ciel', label: 'Bleu ciel' },
      { value: 'vert', label: 'Vert' },
      { value: 'jaune', label: 'Jaune' },
      { value: 'rouge', label: 'Rouge' },
      { value: 'rose', label: 'Rose' },
    ],
  },
  // Developer documentation (the former `description`, kept verbatim):
  // Quantité de padding autour du texte dans chaque cellule de la grille (corps et
  // en-têtes). 'loose' (Ample) est la valeur d'origine px-4/py-3.5 ; 'compact' et 'tight' la
  // réduisent progressivement. Défaut livré : compact (point 6 du grill matrix-view du
  // 2026-08-18).
  {
    key: 'matrix-cell-padding',
    label: 'Espacement des cellules',
    description:
      "Règle l'espace autour du texte dans chaque case de la grille. Serré pour voir plus de lignes à l'écran, ample pour une lecture plus aérée.",
    section: 'matrice',
    kind: 'tweak',
    type: 'enum',
    default: 'compact',
    options: [
      { value: 'loose', label: 'Ample' },
      { value: 'compact', label: 'Compact' },
      { value: 'tight', label: 'Serré' },
    ],
  },
  // Developer documentation (the former `description`, kept verbatim):
  // Fond des en-têtes de colonne de la grille. 'white' (Blanc) est l'en-tête plat gris/blanc
  // d'origine. 'pitch' est le thème fourni initialement dans le pitch du projet : un ton par
  // palier, via des tokens spécifiques à la matrice (sans lien avec la palette des noyaux),
  // mappés par numéro de palier fixe. 'noyau-gradient' (Dégradé noyau) fait passer les
  // en-têtes du blanc (palier 0) à la couleur du noyau réel de l'entreprise (palier 6) par
  // pas réguliers, le palier -10 (sabotage) restant gris. Défaut livré : pitch.
  {
    key: 'matrix-header-theme',
    label: 'Couleur des en-têtes de paliers',
    description:
      "Fond des titres de colonnes : blanc uni, une couleur par palier, ou un dégradé vers la couleur du noyau de l'entreprise. L'export de la matrice suit le même choix.",
    section: 'matrice',
    kind: 'tweak',
    type: 'enum',
    default: 'pitch',
    options: [
      { value: 'white', label: 'Blanc' },
      { value: 'pitch', label: 'Couleurs des paliers' },
      { value: 'noyau-gradient', label: 'Dégradé du noyau' },
    ],
  },
  // Developer documentation (the former `description`, kept verbatim):
  // Si les 6 lignes d'activation affichent leur propre nom de levier, en miroir de l'option
  // `activationLevierNames` de la page d'export. 'hidden' (Masqués) est la grille d'origine
  // : les 6 lignes partagent une seule cellule d'en-tête fusionnée "Ligne d'activation" et
  // aucun nom de levier n'apparaît. 'column' (Colonne dédiée) conserve cette cellule
  // fusionnée et ajoute une seconde colonne de libellés sticky contenant chaque nom.
  // 'inline' (Bande + noms) réduit la cellule fusionnée à une fine bande verticale qui garde
  // la flèche de repli et le menu «…» du bloc (ils n'ont pas d'autre place une fois la large
  // cellule fusionnée supprimée) et laisse toute la colonne de libellés aux noms.
  {
    key: 'matrix-activation-levier-names',
    label: "Noms des leviers d'activation",
    description:
      "Affiche le nom de chacun des 6 leviers sur les lignes d'activation, dans une colonne à part ou à la place du bandeau « Ligne d'activation », réduit à une fine bande.",
    section: 'matrice',
    kind: 'tweak',
    type: 'enum',
    default: 'hidden',
    options: [
      { value: 'hidden', label: 'Masqués' },
      { value: 'column', label: 'Colonne dédiée' },
      { value: 'inline', label: 'Bandeau réduit' },
    ],
  },
  // Developer documentation (the former `description`, kept verbatim):
  // Colore chaque ligne d'activation selon son levier, en utilisant l'ordre des étapes du
  // noyau de l'entreprise (NOYAU_STEP_ORDER[noyau][order-1] → NOYAU_COLOR_TOKEN) — la même
  // dérivation que le réglage singularity-step-decoration de la page entreprise, pour que
  // les deux concordent. 'accent' (Bordure + point) est une bordure gauche plus un point
  // coloré à côté du nom ; 'tint' (Fond teinté) un en-tête de ligne légèrement teinté ;
  // 'bar' (Bande latérale) une bande de six segments colorés le long du bord gauche du bloc.
  // Quand matrix-activation-levier-names vaut 'hidden' il n'y a pas d'en-tête par levier à
  // colorer, donc 'accent' et 'tint' retombent tous deux sur la bande plutôt que de ne rien
  // faire silencieusement.
  {
    key: 'matrix-activation-levier-colors',
    label: "Couleurs des leviers d'activation",
    description:
      "Colore chaque ligne d'activation avec la couleur de son levier, comme les étapes du setup de singularité. Si les noms des leviers sont masqués, seule la bande latérale s'affiche.",
    section: 'matrice',
    kind: 'tweak',
    type: 'enum',
    default: 'off',
    options: [
      { value: 'off', label: 'Aucune' },
      { value: 'accent', label: 'Bordure + point' },
      { value: 'tint', label: 'Fond teinté' },
      { value: 'bar', label: 'Bande latérale' },
    ],
  },
  // Developer documentation (the former `description`, kept verbatim):
  // Décoration de chaque ligne du tableau du setup de singularité, dérivée du noyau de
  // l'étape (lui-même dérivé du noyau sélectionné de l'entreprise). 'accent' affiche une
  // bordure gauche et un point coloré ; 'tint' un fond légèrement teinté ; 'both' les deux ;
  // 'none' aucune décoration. Remplace les deux anciens booléens singularity-step-accent /
  // singularity-step-tint, qui encodaient ce même choix à quatre états sur deux
  // interrupteurs.
  {
    key: 'singularity-step-decoration',
    label: 'Couleur des étapes',
    description:
      'Marque chacune des 6 étapes du setup avec la couleur de son noyau : une bordure et un point, un fond teinté, les deux, ou rien.',
    section: 'setup',
    kind: 'tweak',
    type: 'enum',
    default: 'accent',
    options: [
      { value: 'none', label: 'Aucune' },
      { value: 'accent', label: 'Bordure + point' },
      { value: 'tint', label: 'Fond teinté' },
      { value: 'both', label: 'Les deux' },
    ],
  },
  // Developer documentation (the former `description`, kept verbatim):
  // Affiche l'application comme si l'utilisateur courant avait ce rôle, sans changer
  // l'utilisateur côté backend. Permet de vérifier que l'UI gated par rôle (par exemple le
  // toggle du prompt de récapitulatif réservé aux pms_admin dans le wizard, ou les liens de
  // navigation réservés aux pms_admin — Identifiants Claude et Documentation technique)
  // réagit correctement à chacun des quatre rôles. N'affecte pas les contrôles de rôle côté
  // serveur : un lien devenu visible mène toujours à une page qui répondra 403.
  {
    key: 'user-role-override',
    label: 'Rôle simulé',
    description:
      "Affiche l'app comme la verrait une personne d'un autre rôle, pour vérifier ce qu'elle voit. Vos droits réels ne changent pas : une page interdite reste interdite.",
    section: 'systeme',
    kind: 'tweak',
    type: 'enum',
    default: 'inherit',
    options: [
      { value: 'inherit', label: 'Mon rôle' },
      { value: 'user', label: 'Utilisateur client' },
      { value: 'admin', label: 'Admin client' },
      { value: 'pms_user', label: 'PMS utilisateur' },
      { value: 'pms_admin', label: 'PMS admin' },
    ],
  },
  // Developer documentation (the former `description`, kept verbatim):
  // Quel modèle Claude le moteur de génération passe à `claude --model` lors de l'invocation
  // du CLI. Les valeurs sont des identifiants de modèle figés (`claude-sonnet-5-5`, …) et
  // non les alias courts du CLI (`sonnet`) : un alias est résolu par la version du CLI
  // installée dans l’image, donc le modèle réel changerait à chaque rebuild sans que la
  // valeur enregistrée ne bouge. Un identifiant figé garantit que la valeur affichée,
  // enregistrée et tracée est exactement le modèle appelé.
  {
    key: 'claude-generation-model',
    label: 'Modèle de génération',
    description:
      'Le modèle Claude qui rédige les cellules des matrices. Opus est le plus fin et le plus coûteux, Haiku le plus rapide et le moins coûteux.',
    section: 'systeme',
    kind: 'flag',
    type: 'enum',
    default: 'claude-sonnet-5-5',
    options: [
      { value: 'claude-opus-5-5', label: 'Opus 5.5' },
      { value: 'claude-sonnet-5-5', label: 'Sonnet 5.5' },
      { value: 'claude-haiku-4-5-20251001', label: 'Haiku 4.5' },
    ],
  },
  // Developer documentation (the former `description`, kept verbatim):
  // Quel modèle Claude produit les digests par fichier et la synthèse inter-documents (voir
  // D20 du design du 2026-08-26). Séparé de `claude-generation-model` à dessein : le digest
  // est la phase qui ingère le plus de texte (jusqu’à 200 000 caractères par fichier), donc
  // son coût suit un autre arbitrage que celui de la qualité des cellules — choisir Opus
  // pour la génération ne doit pas imposer de digérer chaque document avec Opus. Défaut
  // prudent malgré tout : le digest est le socle sur lequel tout le reste s’appuie, et un
  // digest médiocre dégrade silencieusement chaque cellule générée. Mêmes identifiants figés
  // que le flag de génération.
  {
    key: 'claude-digest-model',
    label: 'Modèle de lecture des documents',
    description:
      'Le modèle Claude qui lit et résume les documents de contexte avant la génération. Un résumé moins fidèle appauvrit toutes les cellules qui en découlent.',
    section: 'systeme',
    kind: 'flag',
    type: 'enum',
    default: 'claude-sonnet-5-5',
    options: [
      { value: 'claude-opus-5-5', label: 'Opus 5.5' },
      { value: 'claude-sonnet-5-5', label: 'Sonnet 5.5' },
      { value: 'claude-haiku-4-5-20251001', label: 'Haiku 4.5' },
    ],
  },
  // Developer documentation (the former `description`, kept verbatim):
  // Quel hôte Tahiti le backend interroge pour l'authentification : prod ('prod',
  // db.patrickmathieu.net) ou dev ('dev', devdb.patrickmathieu.net). Lu à chaque appel
  // login/logout/status — voir packages/backend/src/lib/tahiti-environment.ts. Surtout
  // exercé sur la page de connexion, mais affecte toutes les requêtes authentifiées (y
  // compris la résolution de l'identité de session), donc non limité à une page. C'est un
  // réglage global, à l’échelle du déploiement : le changer invalide toutes les sessions
  // authentifiées, puisqu’un token émis contre un environnement Tahiti n’est pas reconnu par
  // l’autre — tous les utilisateurs de ce déploiement sont déconnectés, pas seulement celui
  // qui a changé le flag.
  {
    key: 'tahiti-environment',
    label: 'Serveur de connexion (Tahiti)',
    description:
      'Le serveur Tahiti qui vérifie les identifiants : Prod pour le travail réel, Dev pour les essais. Le changer déconnecte immédiatement tous les utilisateurs, vous compris.',
    section: 'systeme',
    kind: 'flag',
    type: 'enum',
    default: 'prod',
    options: [
      { value: 'prod', label: 'Prod' },
      { value: 'dev', label: 'Dev' },
    ],
  },
] as const satisfies readonly Def[];

/** `kind: 'flag'` entries — backend-persisted. */
export const FLAG_DEFS: readonly Def[] = DEFS.filter((def) => def.kind === 'flag');
/** `kind: 'tweak'` entries — localStorage-only. */
export const TWEAK_DEFS: readonly Def[] = DEFS.filter((def) => def.kind === 'tweak');

function flagKeys(): [string, ...string[]] {
  const keys = FLAG_DEFS.map((def) => def.key);
  const [first, ...rest] = keys;
  if (first === undefined) {
    throw new Error('FLAG_DEFS is empty — z.enum requires at least one key.');
  }
  return [first, ...rest];
}

/** Valid keys for `kind: 'flag'` entries — used to validate the backend route's `:key` param. */
export const featureFlagKeySchema = z.enum(flagKeys());

/** A backend-persisted flag's current key/value, as returned by the API. */
export const featureFlagSchema = z.object({
  key: featureFlagKeySchema,
  value: z.string(),
});
export type FeatureFlag = z.infer<typeof featureFlagSchema>;

/** Body of `PATCH /feature-flags/:key`. */
export const setFeatureFlagInputSchema = z.object({
  value: z.string().min(1).max(200),
});
export type SetFeatureFlagInput = z.infer<typeof setFeatureFlagInputSchema>;

/** Looks up a flag's def by key. Callers only pass keys already validated by `featureFlagKeySchema`. */
export function flagDefByKey(key: string): Def {
  const def = FLAG_DEFS.find((d) => d.key === key);
  if (!def) {
    throw new Error(`Unknown feature flag key: '${key}'.`);
  }
  return def;
}

/** Whether `rawValue` is valid for `def` per its `type` (and `options`/`min`/`max` when applicable). */
export function isValidFlagValue(def: Def, rawValue: string): boolean {
  switch (def.type) {
    case 'boolean':
      return rawValue === 'true' || rawValue === 'false';
    case 'enum':
      return def.options.some((option) => option.value === rawValue);
    case 'number': {
      const n = Number(rawValue);
      if (!Number.isFinite(n)) return false;
      if (def.min !== undefined && n < def.min) return false;
      if (def.max !== undefined && n > def.max) return false;
      return true;
    }
    case 'string':
      return true;
  }
}

/** The two Tahiti hosts the backend can authenticate against. Must match the `tahiti-environment` def's option values. */
export const tahitiEnvironmentValueSchema = z.enum(['prod', 'dev']);
export type TahitiEnvironmentValue = z.infer<typeof tahitiEnvironmentValueSchema>;

/** Body and response of `PUT /tahiti-environment`, response of `GET /tahiti-environment`. */
export const tahitiEnvironmentSchema = z.object({ value: tahitiEnvironmentValueSchema });
export type TahitiEnvironment = z.infer<typeof tahitiEnvironmentSchema>;
